import { useEffect, useRef } from "react"

import type { ScannedCode } from "../types"

interface ScanResult {
  success: { scannedCode: ScannedCode } | null
  error: boolean
}

interface UseBarcodeScannerDetectionOptions {
  onScan: (result: ScanResult) => void
  minLength?: number
  timeout?: number
}

const MIN_LENGTH = 10
const TIMEOUT = 100
const EVENT_NAME = "keypress"

export const useScannerDetection = ({ onScan }: UseBarcodeScannerDetectionOptions): void => {
  const scanTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const scanBufferRef = useRef<string>("")

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      scanBufferRef.current += e.key

      if (scanTimeoutRef.current) {
        clearTimeout(scanTimeoutRef.current)
      }

      scanTimeoutRef.current = setTimeout(() => {
        if (scanBufferRef.current.length > MIN_LENGTH) {
          try {
            const scannedCode: ScannedCode = JSON.parse(scanBufferRef.current)
            onScan({ success: { scannedCode }, error: false })
          } catch {
            onScan({ success: null, error: true })
          }
        }
        scanBufferRef.current = ""
      }, TIMEOUT)
    }

    document.addEventListener(EVENT_NAME, handleKeyPress)

    return () => {
      document.removeEventListener(EVENT_NAME, handleKeyPress)
      if (scanTimeoutRef.current) {
        clearTimeout(scanTimeoutRef.current)
      }
    }
  }, [onScan])
}
